/**
 * GoalReadOnlyDetail — presentational, read-only view of a single participant
 * goal for Support Workers (goals.view only). Renders intentional fields plus
 * the SMART criteria, measurement info and an existing progress/measurement
 * timeline. Contains NO mutation calls and NO management controls.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const SMART_LABELS = [
  ['specific', 'Specific'],
  ['measurable', 'Measurable'],
  ['achievable', 'Achievable'],
  ['relevant', 'Relevant'],
  ['time_bound', 'Time-bound'],
];

function Field({ label, value, testid }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div data-testid={testid}>
      <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</h5>
      <p className="text-sm whitespace-pre-wrap">{value}</p>
    </div>
  );
}

export function GoalReadOnlyDetail({ goal, timeline, timelineError }) {
  if (!goal) return null;
  const smart = goal.smart || {};
  const smartRows = SMART_LABELS.filter(([k]) => smart[k]);
  const events = timeline?.events || [];

  return (
    <div className="space-y-4" data-testid="goal-readonly-detail">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {goal.goal_no && <Badge variant="outline" className="font-mono text-[10px]">{goal.goal_no}</Badge>}
            <Badge variant="outline">{goal.status || '—'}</Badge>
            {goal.category && <Badge variant="outline" className="text-[10px]">{goal.category}</Badge>}
            {goal.target_date && <span className="text-xs text-muted-foreground">Target {goal.target_date}</span>}
          </div>
          <h3 className="font-semibold">{goal.title || goal.goal || 'Goal'}</h3>
        </div>
      </div>

      {goal.progress_percent != null && (
        <div data-testid="goal-progress">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Progress</h5>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${Math.max(0, Math.min(100, goal.progress_percent))}%` }} />
            </div>
            <span className="text-sm tabular-nums">{goal.progress_percent}%</span>
          </div>
        </div>
      )}

      <div className="grid gap-3 border-t pt-3">
        <Field label="Description" value={goal.description} testid="goal-description" />
      </div>

      {smartRows.length > 0 && (
        <div className="border-t pt-3 space-y-2" data-testid="goal-smart">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">SMART Criteria</h5>
          {smartRows.map(([k, label]) => (
            <div key={k} data-testid={`goal-smart-${k}`}>
              <span className="text-xs font-medium text-muted-foreground">{label}: </span>
              <span className="text-sm whitespace-pre-wrap">{smart[k]}</span>
            </div>
          ))}
        </div>
      )}

      {(goal.measurement_type || goal.measurement_config) && (
        <div className="border-t pt-3 space-y-1" data-testid="goal-measurement">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Measurement</h5>
          {goal.measurement_type && <p className="text-sm">Type: {goal.measurement_type}</p>}
          {goal.measurement_config?.unit && <p className="text-sm">Unit: {goal.measurement_config.unit}</p>}
          {goal.measurement_config?.baseline != null && <p className="text-sm">Baseline: {goal.measurement_config.baseline}</p>}
          {goal.measurement_config?.target != null && <p className="text-sm">Target: {goal.measurement_config.target}</p>}
        </div>
      )}

      <div className="border-t pt-3" data-testid="goal-timeline">
        <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Progress history</h5>
        {timelineError ? (
          <p className="text-xs text-muted-foreground italic" data-testid="goal-timeline-error">Progress history unavailable.</p>
        ) : events.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid="goal-timeline-empty">No progress history recorded.</p>
        ) : (
          <ul className="space-y-1.5">
            {events.map((e, i) => (
              <li key={i} className="text-sm flex items-start gap-2" data-testid={`goal-timeline-event-${i}`}>
                <Badge variant="outline" className="text-[10px] mt-0.5">{e.type === 'MEASUREMENT' ? 'Measurement' : 'Progress'}</Badge>
                <span className="min-w-0">
                  {e.progress_percent != null && <span>{e.progress_percent}% </span>}
                  {e.measurement_type && <span>{e.measurement_type}{e.value != null ? `: ${e.value}` : ''} </span>}
                  {e.note && <span className="text-muted-foreground">— {e.note}</span>}
                  {e.created_at && <span className="block text-[10px] text-muted-foreground">{String(e.created_at).slice(0, 16)}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default GoalReadOnlyDetail;
